import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

import ActivityLogService from "src/Services/API/activitylog.service";
import { getAvatarUrl, getImageUrl } from "src/Common/common";
import { handleError } from "src/Common/function";

// techStatus 1..4 -> label + dot colour, matching the status board columns.
const STATUS_META = {
  1: { label: "CHECKED OUT", color: "#334155", dotColor: "#868DA6" },
  2: { label: "ENROUTE", color: "#C2410C", dotColor: "#F97316" },
  3: { label: "ON SITE", color: "#15803D", dotColor: "#22C55E" },
  4: { label: "GETTING PARTS", color: "#7E22CE", dotColor: "#A855F7" },
};

// Range tabs -> number of days passed to the backend feed query.
const RANGES = [
  { key: 1, label: "Last 24 Hours" },
  { key: 7, label: "Last 7 Days" },
  { key: 30, label: "Last 30 Days" },
];

// Title prefix for the activity-summary card, keyed by the selected range.
const SUMMARY_TITLE = {
  1: "LAST 24 HOURS ACTIVITY SUMMARY",
  7: "LAST 7 DAYS ACTIVITY SUMMARY",
  30: "LAST 30 DAYS ACTIVITY SUMMARY",
};

const TECH_STATUS_LABELS = {
  1: "Checked Out",
  2: "Enroute",
  3: "On Site",
  4: "Getting Parts",
};

// Build the human-readable activity line + linked WO number per row. The
// backend stores a `description` and structured `meta`; we prefer a richer,
// design-matching sentence and fall back to description.
const describeActivity = (item) => {
  const meta = item.meta || {};
  switch (item.action) {
    case "TECH_STATUS_CHANGED":
      return {
        text: (
          <>
            Status changed to <span className="font-[600]">{TECH_STATUS_LABELS[meta.to] ?? "—"}</span>
          </>
        ),
        woNumber: meta.woNumber,
        woId: meta.woId,
      };
    case "UPLOAD_ADDED":
      return {
        text: (
          <>
            Uploaded <span className="font-[600]">{meta.fileName ?? "file"}</span>
          </>
        ),
        woNumber: meta.woNumber,
        woId: meta.woId ?? item.targetId,
      };
    case "PO_ISSUED":
      return {
        text: (
          <>
            PO <span className="font-[600]">#{meta.poNumber}</span> issued
          </>
        ),
        woNumber: meta.woNumber,
        woId: meta.woId,
      };
    case "PO_COMPLETED":
      return {
        text: (
          <>
            PO <span className="font-[600]">#{meta.poNumber}</span> completed
          </>
        ),
        woNumber: meta.woNumber,
        woId: meta.woId,
      };
    case "HOURS_LOGGED":
      return {
        text: (
          <>
            Logged <span className="font-[600]">{meta.totalHours ?? 0} hours</span>
          </>
        ),
        woNumber: meta.woNumber,
        woId: meta.woId,
      };
    case "WO_ISSUED":
      return {
        text: (
          <>
            Issued <span className="font-[600]">WO-{meta.woNumber}</span>
          </>
        ),
        woNumber: meta.woNumber,
        woId: item.targetId,
      };
    case "WO_ACCEPTED":
      return {
        text: (
          <>
            Accepted <span className="font-[600]">WO-{meta.woNumber}</span>
          </>
        ),
        woNumber: meta.woNumber,
        woId: item.targetId,
      };
    default:
      return {
        text: item.description ?? item.action,
        woNumber: meta.woNumber,
        woId: meta.woId ?? item.targetId,
      };
  }
};

// Section label (TODAY / YESTERDAY / Mon DD, YYYY) for a given timestamp.
const dayHeading = (date) => {
  const d = moment(date);
  if (d.isSame(moment(), "day")) return "TODAY";
  if (d.isSame(moment().subtract(1, "day"), "day")) return "YESTERDAY";
  return d.format("MMM D, YYYY").toUpperCase();
};

// Right-hand timestamp: "x min ago" for today, "Yesterday, h:mm A", else date.
const timeLabel = (date) => {
  const d = moment(date);
  if (d.isSame(moment(), "day")) return d.fromNow();
  if (d.isSame(moment().subtract(1, "day"), "day")) return `Yesterday, ${d.format("h:mm A")}`;
  return d.format("MMM D, h:mm A");
};

function TechActivityDrawer({ isOpen, tech, onClose }) {
  const [days, setDays] = useState(1);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const drawerRef = useRef(null);

  const actorId = tech?.user?.id;

  const loadFeed = useCallback(() => {
    if (!actorId) return;
    setIsLoading(true);
    ActivityLogService.getActorFeed(actorId, days)
      .then((response) => {
        if (response.status === 200) {
          setItems(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch((error) => handleError(error))
      .finally(() => setIsLoading(false));
  }, [actorId, days]);

  // Reset to the default range each time a different tech is opened.
  useEffect(() => {
    if (isOpen) setDays(1);
  }, [isOpen, actorId]);

  useEffect(() => {
    if (isOpen && actorId) loadFeed();
  }, [isOpen, actorId, loadFeed]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Group the flat feed by day, preserving the newest-first order.
  const groups = useMemo(() => {
    const result = [];
    let current = null;
    items.forEach((item) => {
      const heading = dayHeading(item.createdAt);
      if (!current || current.heading !== heading) {
        current = { heading, items: [] };
        result.push(current);
      }
      current.items.push(item);
    });
    return result;
  }, [items]);

  // Aggregate counts for the summary card from the feed already loaded for the
  // selected range. Work Orders = issued + accepted; Purchase Orders = issued +
  // completed; Hours = sum of the totalHours on every HOURS_LOGGED event.
  const summary = useMemo(() => {
    let workOrders = 0;
    let purchaseOrders = 0;
    let hours = 0;
    items.forEach((item) => {
      switch (item.action) {
        case "WO_ISSUED":
        case "WO_ACCEPTED":
          workOrders += 1;
          break;
        case "PO_ISSUED":
        case "PO_COMPLETED":
          purchaseOrders += 1;
          break;
        case "HOURS_LOGGED":
          hours += Number(item.meta?.totalHours) || 0;
          break;
        default:
          break;
      }
    });
    return { workOrders, purchaseOrders, hours };
  }, [items]);

  const statusMeta = STATUS_META[tech?.techStatus] ?? STATUS_META[1];

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-40 bg-black bg-opacity-20 transition-opacity" />}
      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 bottom-0 z-50 flex h-screen w-[474px] max-w-full transform flex-col bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <DrawerAvatar user={tech?.user} dotColor={statusMeta.dotColor} />
            <div className="min-w-0">
              <div className="font-OpenSansRegular text-[18px] font-[600] capitalize leading-[28px] text-[#1A2332]">
                {tech?.user?.name ?? "—"}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 font-OpenSansRegular text-[13px] leading-[18px]">
                <span className="font-[700] uppercase tracking-[0.13px]" style={{ color: statusMeta.color }}>
                  {statusMeta.label}
                </span>
                <span className="text-[#D0D5DD]">•</span>
                <span className="font-OpenSansRegular text-[14px] font-[400] leading-[20px] tracking-[0.14px] text-[#868DA6]">
                  {tech?.wo?.branch ?? "—"}
                </span>
                <span className="text-[#D0D5DD]">•</span>
                <span className="font-OpenSansRegular text-[14px] font-[400] leading-[20px] tracking-[0.14px] text-[#868DA6]">
                  {tech?.user?.roles?.[0]?.name ?? "—"}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="-mr-1 flex h-8 w-8 items-center justify-center rounded-full text-[#98A2B3] hover:bg-[#F2F4F7]"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Active work order */}
        {tech?.wo?.number && (
          <div className="px-6">
            <a
              href={tech.wo.id ? `/work/${tech.wo.id}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between rounded-[8px] border border-[#DAEBFF] bg-[#EAF3FF] px-4 py-3 transition-colors hover:bg-[#E0EDFF]"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-[4px] bg-[#C8E1FF]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M6.5335 2.5C6.51509 2.5 6.50016 2.51492 6.50016 2.53333V3.5H9.50016V2.53333C9.50016 2.51492 9.48524 2.5 9.46683 2.5H6.5335ZM10.5002 3.5V2.53333C10.5002 1.96264 10.0375 1.5 9.46683 1.5H6.5335C5.9628 1.5 5.50016 1.96264 5.50016 2.53333V3.5H2.9335C1.7737 3.5 0.833496 4.4402 0.833496 5.6V11.7333C0.833496 12.8931 1.7737 13.8333 2.9335 13.8333H13.0668C14.2266 13.8333 15.1668 12.8931 15.1668 11.7333V5.6C15.1668 4.4402 14.2266 3.5 13.0668 3.5H10.5002ZM2.9335 4.5C2.32598 4.5 1.8335 4.99249 1.8335 5.6V8.16667H6.16683V7.86667C6.16683 7.29597 6.62947 6.83333 7.20016 6.83333H8.80016C9.37086 6.83333 9.8335 7.29597 9.8335 7.86667V8.16667H14.1668V5.6C14.1668 4.99249 13.6743 4.5 13.0668 4.5H2.9335ZM14.1668 9.16667H9.8335V9.46667C9.8335 10.0374 9.37086 10.5 8.80016 10.5H7.20016C6.62947 10.5 6.16683 10.0374 6.16683 9.46667V9.16667H1.8335V11.7333C1.8335 12.3408 2.32598 12.8333 2.9335 12.8333H13.0668C13.6743 12.8333 14.1668 12.3408 14.1668 11.7333V9.16667ZM7.16683 9.46667C7.16683 9.48508 7.18175 9.5 7.20016 9.5H8.80016C8.81857 9.5 8.8335 9.48508 8.8335 9.46667V7.86667C8.8335 7.84826 8.81857 7.83333 8.80016 7.83333H7.20016C7.18175 7.83333 7.16683 7.84826 7.16683 7.86667V9.46667Z"
                      fill="#0065FD"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-OpenSansRegular text-[10px] font-[600] uppercase leading-[15px] tracking-[-0.25px] text-[#73A7FF]">
                    Active Work Order
                  </div>
                  <div className="font-OpenSansRegular text-[14px] font-[600] leading-[20px] tracking-[0.14px] text-[#0065FD]">
                    WO-{tech.wo.number}
                  </div>
                  {(tech.wo?.customer || tech.wo?.location) && (
                    <div className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A] capitalize line-clamp-2">
                      {[tech.wo?.customer, tech.wo?.location].filter(Boolean).join(" - ")}
                    </div>
                  )}
                </div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
                <path
                  d="M5.78145 4.72207C6.07441 5.01504 6.07441 5.49082 5.78145 5.78379L1.28145 10.2838C0.988476 10.5768 0.512695 10.5768 0.219727 10.2838C-0.0732422 9.99082 -0.0732422 9.51504 0.219727 9.22207L4.19004 5.25176L0.22207 1.28145C-0.0708985 0.988477 -0.0708985 0.512695 0.22207 0.219727C0.515039 -0.0732422 0.99082 -0.0732422 1.28379 0.219727L5.78379 4.71973L5.78145 4.72207Z"
                  fill="#98C1FF"
                />
              </svg>
            </a>
          </div>
        )}

        <div className="mt-5 h-px bg-[#EAECF0]" />

        {/* Range tabs */}
        <div className="px-6 pt-4 pb-4">
          <div className="relative flex items-center rounded-[24px] bg-[#EEF0F9] p-1">
            {/* Sliding indicator behind the active tab */}
            <div
              className="absolute top-1 bottom-1 left-1 rounded-[24px] bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] transition-transform duration-300 ease-out"
              style={{
                width: `calc((100% - 8px) / ${RANGES.length})`,
                transform: `translateX(${RANGES.findIndex((r) => r.key === days) * 100}%)`,
              }}
            />
            {RANGES.map((range) => (
              <button
                key={range.key}
                onClick={() => setDays(range.key)}
                className={`relative z-10 flex-1 rounded-[24px] py-2 text-center font-OpenSansRegular text-[14px] font-[600] leading-[20px] tracking-[0.14px] capitalize transition-colors ${
                  days === range.key ? "text-[#1A2332]" : "text-[#7C829A]"
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Activity summary card — only for the Last 7 / Last 30 Days ranges */}
        {days !== 1 && (
        <div className="px-6 pb-4">
          <div className="mb-3 font-OpenSansRegular text-[12px] font-[600] leading-[18px] uppercase tracking-[0.12px] text-[#868DA6]">
            {SUMMARY_TITLE[days] ?? "ACTIVITY SUMMARY"}
          </div>
          <div className="grid grid-cols-3 gap-[8px]">
            <SummaryTile
              label="Work Orders"
              value={summary.workOrders}
              iconBg="#C8E1FF"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <g clip-path="url(#clip0_5404_24710)">
                    <path
                      d="M4.3125 1.125H7.6875C7.79063 1.125 7.875 1.20938 7.875 1.3125V2.25H4.125V1.3125C4.125 1.20938 4.20937 1.125 4.3125 1.125ZM3 1.3125V2.25H1.5C0.672656 2.25 0 2.92266 0 3.75V6H4.5H7.5H12V3.75C12 2.92266 11.3273 2.25 10.5 2.25H9V1.3125C9 0.588281 8.41172 0 7.6875 0H4.3125C3.58828 0 3 0.588281 3 1.3125ZM12 6.75H7.5V7.5C7.5 7.91484 7.16484 8.25 6.75 8.25H5.25C4.83516 8.25 4.5 7.91484 4.5 7.5V6.75H0V9.75C0 10.5773 0.672656 11.25 1.5 11.25H10.5C11.3273 11.25 12 10.5773 12 9.75V6.75Z"
                      fill="#0065FD"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_5404_24710">
                      <path d="M0 0H12V12H0V0Z" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              }
            />
            <SummaryTile
              label="Purchase Orders"
              value={summary.purchaseOrders}
              iconBg="#F3E8FF"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="9" height="12" viewBox="0 0 9 12" fill="none">
                  <g clip-path="url(#clip0_5404_24719)">
                    <path
                      d="M1.5 0C0.672656 0 0 0.672656 0 1.5V10.5C0 11.3273 0.672656 12 1.5 12H7.5C8.32734 12 9 11.3273 9 10.5V3.75H6C5.58516 3.75 5.25 3.41484 5.25 3V0H1.5ZM6 0V3H9L6 0ZM1.875 1.5H3.375C3.58125 1.5 3.75 1.66875 3.75 1.875C3.75 2.08125 3.58125 2.25 3.375 2.25H1.875C1.66875 2.25 1.5 2.08125 1.5 1.875C1.5 1.66875 1.66875 1.5 1.875 1.5ZM1.875 3H3.375C3.58125 3 3.75 3.16875 3.75 3.375C3.75 3.58125 3.58125 3.75 3.375 3.75H1.875C1.66875 3.75 1.5 3.58125 1.5 3.375C1.5 3.16875 1.66875 3 1.875 3ZM2.25 5.25H6.75C7.16484 5.25 7.5 5.58516 7.5 6V7.5C7.5 7.91484 7.16484 8.25 6.75 8.25H2.25C1.83516 8.25 1.5 7.91484 1.5 7.5V6C1.5 5.58516 1.83516 5.25 2.25 5.25ZM2.25 6V7.5H6.75V6H2.25ZM5.625 9.75H7.125C7.33125 9.75 7.5 9.91875 7.5 10.125C7.5 10.3313 7.33125 10.5 7.125 10.5H5.625C5.41875 10.5 5.25 10.3313 5.25 10.125C5.25 9.91875 5.41875 9.75 5.625 9.75Z"
                      fill="#A855F7"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_5404_24719">
                      <path d="M0 0H9V12H0V0Z" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              }
            />
            <SummaryTile
              label="Hours Logged"
              value={`${summary.hours}h`}
              iconBg="#FFEDD5"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <g clip-path="url(#clip0_5404_24728)">
                    <path
                      d="M6 0C7.5913 0 9.11742 0.632141 10.2426 1.75736C11.3679 2.88258 12 4.4087 12 6C12 7.5913 11.3679 9.11742 10.2426 10.2426C9.11742 11.3679 7.5913 12 6 12C4.4087 12 2.88258 11.3679 1.75736 10.2426C0.632141 9.11742 0 7.5913 0 6C0 4.4087 0.632141 2.88258 1.75736 1.75736C2.88258 0.632141 4.4087 0 6 0ZM5.4375 2.8125V6C5.4375 6.1875 5.53125 6.36328 5.68828 6.46875L7.93828 7.96875C8.19609 8.14219 8.54531 8.07187 8.71875 7.81172C8.89219 7.55156 8.82187 7.20469 8.56172 7.03125L6.5625 5.7V2.8125C6.5625 2.50078 6.31172 2.25 6 2.25C5.68828 2.25 5.4375 2.50078 5.4375 2.8125Z"
                      fill="#F97316"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_5404_24728">
                      <path d="M0 0H12V12H0V0Z" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              }
            />
          </div>
        </div>
        )}

        <div className="h-px bg-[#EAECF0]" />

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="py-10 text-center font-OpenSansRegular text-[13px] text-[#98A2B3]">Loading…</div>
          ) : groups.length === 0 ? (
            <div className="py-10 text-center font-OpenSansRegular text-[13px] text-[#98A2B3]">
              No activity in this period
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.heading} className="mb-2">
                <div className="flex items-center gap-3 py-3">
                  <span className="font-OpenSansRegular text-[12px] font-[600] uppercase tracking-[0.6px] text-[#98A2B3]">
                    {group.heading}
                  </span>
                  <span className="h-px flex-1 bg-[#EAECF0]" />
                </div>
                <div className="flex flex-col">
                  {group.items.map((item, index) => (
                    <ActivityRow key={item.id} item={item} isLast={index === group.items.length - 1} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default TechActivityDrawer;

// A single timeline row. The connecting line is drawn behind the dot so the
// whole day-group reads as one vertical thread.
const ActivityRow = ({ item, isLast }) => {
  const { text, woNumber, woId } = describeActivity(item);
  const customer = item.meta?.customer;
  const location = item.meta?.location;
  const woContext = [customer, location].filter(Boolean).join(" - ");

  return (
    <div className="relative flex gap-3">
      {/* Dot + connector */}
      <div className="relative flex flex-col items-center">
        {/* <span className="z-10 mt-1 h-[26px] w-[26px] flex-shrink-0 rounded-full border border-[#EAECF0] bg-white" /> */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5Z"
            stroke="#0065FD"
          />
          <path
            d="M8 10.5C8.66304 10.5 9.29893 10.2366 9.76777 9.76777C10.2366 9.29893 10.5 8.66304 10.5 8C10.5 7.33696 10.2366 6.70107 9.76777 6.23223C9.29893 5.76339 8.66304 5.5 8 5.5C7.33696 5.5 6.70107 5.76339 6.23223 6.23223C5.76339 6.70107 5.5 7.33696 5.5 8C5.5 8.66304 5.76339 9.29893 6.23223 9.76777C6.70107 10.2366 7.33696 10.5 8 10.5ZM8 7.0625C8.24864 7.0625 8.4871 7.16127 8.66291 7.33709C8.83873 7.5129 8.9375 7.75136 8.9375 8C8.9375 8.24864 8.83873 8.4871 8.66291 8.66291C8.4871 8.83873 8.24864 8.9375 8 8.9375C7.75136 8.9375 7.5129 8.83873 7.33709 8.66291C7.16127 8.4871 7.0625 8.24864 7.0625 8C7.0625 7.75136 7.16127 7.5129 7.33709 7.33709C7.5129 7.16127 7.75136 7.0625 8 7.0625Z"
            fill="#0065FD"
          />
        </svg>

        {!isLast && <span className="-mt-[-3px] -mb-[-3px] w-px flex-1 bg-[#DAEBFF]" />}
      </div>

      {/* Content */}
      <div className="flex flex-1 items-start justify-between gap-3 pb-5">
        <div className="min-w-0">
          <div className="font-OpenSansRegular text-[14px] font-[600] leading-[20px] tracking-[0.14px] text-[#1A2332]">
            {text}
          </div>
          {woNumber && (
            <a
              href={woId ? `/work/${woId}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="block font-OpenSansSemiBold text-[12px] font-[600] leading-[18px] tracking-[0.12px] text-[#0065FD] hover:underline"
            >
              WO-{woNumber}
            </a>
          )}
          {woContext && (
            <div className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6] capitalize line-clamp-2">
              {woContext}
            </div>
          )}
        </div>
        <span className="whitespace-nowrap pt-0.5 font-OpenSansRegular text-[12px] font-normal leading-[18px] tracking-[0.12px] text-[#868DA6]">
          {timeLabel(item.createdAt)}
        </span>
      </div>
    </div>
  );
};

// One stat tile inside the activity-summary card: label + value on the left,
// a coloured icon chip on the right.
const SummaryTile = ({ label, value, icon, iconBg }) => (
  <div className="flex items-center justify-between rounded-[10px] border border-[#E5E8F3] px-[10px] h-[60px]">
    <div className="min-w-0">
      <div className="truncate text-center font-OpenSansRegular text-[10px] font-[400] leading-[14px] text-[#7C829A]">
        {label}
      </div>
      <div className="font-OpenSansRegular text-[16px] font-[600] capitalize leading-[24px] text-[#1A2332]">
        {value}
      </div>
    </div>
    <span
      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[8px]"
      style={{ background: iconBg }}
    >
      {icon}
    </span>
  </div>
);

const DrawerAvatar = ({ user, dotColor }) => {
  const [errored, setErrored] = useState(false);
  const showImage = user?.avatar && !errored;

  return (
    <div className="relative">
      {showImage ? (
        <img
          src={getAvatarUrl(user.avatar)}
          onError={() => setErrored(true)}
          className="h-[48px] w-[48px] rounded-full object-cover"
          alt={user?.name ?? ""}
        />
      ) : (
        <img
          src={getImageUrl("/images/common/avatar_default.png")}
          className="h-[48px] w-[48px] rounded-full object-cover"
          alt=""
        />
      )}
      <span
        className="absolute bottom-0 right-0 h-[14px] w-[14px] rounded-full border-2 border-white"
        style={{ background: dotColor }}
      />
    </div>
  );
};
